import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import PaymentKpi from "./PaymentKpi";
import AddEditPaymentModalContent from "./AddEditPaymentModalContent";
import Modal from "../../../../components/Modal";
import Button from "../../../../components/Button";
import TableBox from "../../../../components/TableBox";
import { AddEdit } from "../../../../interfaces/payment.interface";
import PaymentHeader from "./PaymentHeader";
import { Validation } from "../../../../interfaces/common";
import { AuthContext } from '../../../../contexts/AuthContext';
import { PermissionModulesEnum, PermissionActionsEnum, Modules } from "../../../../enums";
import { hasResourseAccess } from '../../../../utils/methods';

interface PaymentsTabProps {
  kpis: any;
  open: {
    addEdit: boolean;
    delete: boolean;
  };
  edit: boolean;
  loading: boolean;
  view: boolean;
  tableValues: any;
  formData: AddEdit;
  validation: Validation;
  methods: any[];
  allMethods: any[];
  accounts: any[];
  allAccounts: any[];
  paymentTypeOptions: any[];
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  onAddClick: () => void;
  onDelete: () => void;
  onDeleteClose: () => void;
  onInputChange: (event: any, name: string) => void;
  onInputBlur: (event: any, name: string) => void;
  onSubmit: (e?: React.FormEvent) => void;
  onAddEditClose: () => void;
}

const PaymentsTab: React.FC<PaymentsTabProps> = ({
  kpis,
  open,
  edit,
  loading,
  view,
  tableValues,
  formData,
  validation,
  methods,
  allMethods,
  accounts,
  allAccounts,
  paymentTypeOptions,
  dropdownSearchData,
  onDropdownSearchChange,
  onAddClick,
  onDelete,
  onDeleteClose,
  onInputChange,
  onInputBlur,
  onSubmit,
  onAddEditClose
}) => {
  const { t } = useTranslation();
  const { isSystemAdmin, permissions } = useContext(AuthContext);

  const hasAddAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Payments]: [PermissionActionsEnum.Add] }] })

  const deleteModalActions = (
    <>
      <Button
        className="btn btn-outline-secondary me-3"
        text={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.deletePayment.buttons.cancel')}
        onClick={onDeleteClose}
        disabled={loading}
      />
      <Button
        className="btn btn-danger"
        text={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.deletePayment.buttons.delete')}
        onClick={onDelete}
        loading={loading}
      />
    </>
  );

  const addEditModalActions = (
    <>
      <Button
        className="btn btn-primary me-3"
        text={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.buttons.save')}
        loading={loading}
        onClick={onSubmit}
      />
      <Button
        className="btn btn-outline-secondary me-3"
        text={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.buttons.cancel')}
        onClick={onAddEditClose}
        disabled={loading}
      />
    </>
  );

  return (
    <div className="card-body">
      {hasAddAccess && <PaymentHeader onAddClick={onAddClick} view={view} />}

      <PaymentKpi kpis={kpis} />

      <TableBox tableValues={tableValues} />

      <Modal
        open={open.addEdit}
        onClose={onAddEditClose}
        title={edit ? t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.editTitle') : t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.title')}
        titleClassName='text-start'
        actionClassName='justify-content-start'
        content={
          <AddEditPaymentModalContent
            formData={formData}
            validation={validation}
            methods={methods}
            allMethods={allMethods}
            accounts={accounts}
            allAccounts={allAccounts}
            paymentTypeOptions={paymentTypeOptions}
            dropdownSearchData={dropdownSearchData}
            onDropdownSearchChange={onDropdownSearchChange}
            onInputChange={onInputChange}
            onInputBlur={onInputBlur}
          />
        }
        action={addEditModalActions}
        maxWidth="md"
      />

      <Modal
        open={open.delete}
        onClose={onDeleteClose}
        title={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.deletePayment.title')}
        content={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.deletePayment.message')}
        action={deleteModalActions}
        contentClassName="text-center"
        maxWidth="sm"
      />
    </div>
  );
};

export default PaymentsTab;